export const ROOM_CANCELLATION_PATH = "/account/room-cancellations";
export const roomCancellationEnabled = (env = process.env) =>
  env.REACT_APP_ROOM_CANCELLATION_ENABLED === "true";
export const cancellationUrl = (params = {}) =>
  `${ROOM_CANCELLATION_PATH}${Object.values(params).some(Boolean) ? `?${new URLSearchParams(Object.entries(params).filter(([, v]) => v))}` : ""}`;
export const money = (value) =>
  Number.isSafeInteger(value)
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(value / 100)
    : "—";
export const dateTime = (value) =>
  (
    typeof value === "number"
      ? Number.isFinite(value)
      : Number.isFinite(Date.parse(value))
  )
    ? new Intl.DateTimeFormat("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
        timeZone: "Asia/Kolkata",
      }).format(new Date(value)) + " IST"
    : "—";
export const guestText = (g = {}) =>
  `${g.adults || 0} adults · ${g.children || 0} children · ${g.infants || 0} infants`;
export const roomName = (value) =>
  /^room\b/i.test(String(value)) ? String(value) : `Room ${value}`;
export function selectionInput(eligibility, selected, reasonCode, reason) {
  if (!eligibility || reason.trim().length < 10 || reason.trim().length > 1000)
    return null;
  const items = [];
  for (const stay of eligibility.items) {
    const unitIds = selected[stay.referenceCode] || [];
    if (!unitIds.length) continue;
    if (
      stay.eligible === false ||
      !stay.proportionalQuantities.includes(unitIds.length) ||
      new Set(unitIds).size !== unitIds.length ||
      unitIds.some((id) => !stay.selectableUnits.some((u) => u.unitId === id))
    )
      return null;
    const remainingGuests = Object.fromEntries(
      ["adults", "children", "infants"].map((k) => [
        k,
        ((stay.guests[k] || 0) * (stay.activeQuantity - unitIds.length)) /
          stay.activeQuantity,
      ]),
    );
    if (
      Object.values(remainingGuests).some((n) => !Number.isInteger(n) || n < 0)
    )
      return null;
    items.push({
      bookingReferenceCode: stay.referenceCode,
      cancelQuantity: unitIds.length,
      unitIds: [...unitIds].sort(),
      remainingGuests,
    });
  }
  return items.length
    ? {
        cartReferenceCode: eligibility.cartReferenceCode,
        items,
        reasonCode,
        reason: reason.trim(),
        treatment: "policy",
      }
    : null;
}
export const operationExpired = (op, now = Date.now()) =>
  op?.state !== "committed" &&
  (!Number.isFinite(Date.parse(op?.expiresAt)) ||
    Date.parse(op.expiresAt) <= now);
export const canConfirm = (op, caps, now) =>
  Boolean(
    caps?.canCommit &&
    op?.canContinue &&
    ["verified", "approved"].includes(op.state) &&
    !operationExpired(op, now),
  );
export const cashConserved = (p) => {
  if (!p) return false;
  const parts = [
    p.priorApprovedRefundPaise ?? 0,
    p.priorCancellationChargePaise ?? 0,
    p.refundPaise,
    p.cancellationChargePaise,
    p.remainingPaidPaise,
  ];
  return (
    [p.capturedPaise, ...parts].every(
      (amount) => Number.isSafeInteger(amount) && amount >= 0,
    ) && p.capturedPaise === parts.reduce((total, amount) => total + amount, 0)
  );
};
export const newKey = () => `rc-${window.crypto.randomUUID()}`;
export async function registrationId(actorId, key, cryptoApi = window.crypto) {
  const digest = await cryptoApi.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(
      JSON.stringify(["room-cancellation-v2", actorId, key]),
    ),
  );
  return (
    "PC-" +
    [...new Uint8Array(digest)]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 40)
  );
}
// Operation identity makes action retries stable across reloads. OTP contents are never persisted.
export const actionKey = (id, action) => `public-${id}-${action}`;
export const pendingSlot = (actorId) => `room-cancellation-pending:${actorId}`;
export function readPending(actorId, storage = sessionStorage) {
  try {
    const p = JSON.parse(storage.getItem(pendingSlot(actorId)));
    return p?.actorId === actorId ? p : null;
  } catch {
    return null;
  }
}
export function clearPending(storage = sessionStorage) {
  for (let i = storage.length - 1; i >= 0; i--) {
    const key = storage.key(i);
    if (key.startsWith("room-cancellation-pending:")) storage.removeItem(key);
  }
}
export function safeDocumentUrl(value) {
  try {
    const u = new URL(value);
    return u.protocol === "https:" ? u.href : null;
  } catch {
    return null;
  }
}
export const documentName = (d) =>
  `${{ cart: "Updated cart receipt", stay: "Updated stay receipt", credit_note: "Credit note", exempt_credit: "Exempt charge adjustment", charge_invoice: "Cancellation charge invoice" }[d.kind] || "Cancellation document"}${d.reference ? ` · ${d.reference}` : ""}`;
export function errorMessage(e) {
  if (e?.status === 429)
    return "Too many attempts. Please wait before trying again. Your existing request is saved.";
  const messages = {
    stale_preview:
      "Your booking or its policy calculation changed. Start a fresh preview before confirming.",
    request_expired:
      "This request expired. Start a fresh preview. No cancellation can be confirmed with this request.",
    cancellation_disabled:
      "Online cancellation is not available for this booking. Please contact support.",
    provider_acceptance_required:
      "Online cancellation is temporarily unavailable. Please contact support.",
    provider_acceptance_invalid:
      "Online cancellation is temporarily unavailable. Please contact support.",
    invalid_otp:
      "The code is invalid or expired. Check the code or request another one.",
    wrong_actor:
      "This request was started by support. You can follow its progress here; contact support to change it.",
    forbidden: "Your account access changed. Please sign in again.",
    not_found: "This booking or request is not available for your account.",
  };
  return (
    messages[e?.data?.code] ||
    (e?.status < 500 && e?.message) ||
    "We could not confirm the response. Refresh this saved request before retrying. Do not start a duplicate cancellation."
  );
}
export const refundText = (status) =>
  ({
    not_queued: "Not submitted",
    not_required: "No cash refund under the policy",
    queued: "Refund queued",
    submitting: "Refund being submitted",
    pending: "Refund processing",
    processed: "Refund processed by payment provider",
    outcome_unknown: "Refund confirmation pending — support is checking",
    failed_review_required: "Refund needs support review",
  })[status] || "Refund status pending";
